import { Component, OnInit } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  AttachedDozent,
  DozentBot,
  LinkedTutorSummary,
  Tutor,
  TutorService,
} from '../../../services/tutor.service';

/**
 * Admin overview for dozent-bots (alpha_dozent template).
 *
 * Lists all dozent-bots the current user can see (admins: all client
 * dozents; non-admins: their own via `UserBotAccess`). Selecting one
 * opens a side panel with the dozent's settings link + a checkbox list
 * of linked tutors. Toggling a checkbox calls `attachDozent` /
 * `detachDozent` on the tutor side.
 *
 * Linked-tutor checkbox states:
 *  - checked + enabled  → already linked to THIS dozent
 *  - unchecked + enabled → no link, free to attach to this dozent
 *  - checked + disabled → already linked to a DIFFERENT dozent
 *    (we don't allow stealing — the admin must detach there first)
 */
@Component({
  selector: 'app-dozenten',
  templateUrl: './dozenten.component.html',
  styleUrl: './dozenten.component.scss',
})
export class DozentenComponent implements OnInit {
  dozents: DozentBot[] = [];
  selectedDozent: DozentBot | null = null;

  /** All tutor-bots in the client (alpha_tutor / alpha_flexible). */
  tutorCandidates: Tutor[] = [];
  /**
   * Map tutor_id → currently-attached dozent_id (from listLinkedTutors
   * across all dozents we know about). Used to gray out checkboxes for
   * tutors already linked to a different dozent.
   */
  tutorAttachments: Record<string, string> = {};

  loading = false;

  constructor(
    public tutorService: TutorService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.refreshDozents();
    this.tutorService.listTutors().subscribe(() => {
      this.tutorCandidates = this.tutorService.tutors$.value.filter(
        (t) => this.isTutorTemplate(t)
      );
      this.refreshAttachments();
    });
  }

  private isTutorTemplate(tutor: Tutor): boolean {
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === tutor.template_id
    );
    return !!template && (template.name === 'alpha_tutor' || template.name === 'alpha_flexible');
  }

  private refreshDozents() {
    this.loading = true;
    this.tutorService.listDozentBots().subscribe({
      next: (list) => {
        this.dozents = list;
        if (!this.selectedDozent && list.length) {
          this.selectDozent(list[0]);
        }
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  /** Pull each dozent's linked tutors so we know which checkboxes to
   *  gray out for the active dozent's panel. */
  private async refreshAttachments() {
    const map: Record<string, string> = {};
    for (const tutor of this.tutorCandidates) {
      try {
        const a: AttachedDozent | null = await firstValueFrom(
          this.tutorService.getAttachedDozent(tutor.id)
        );
        if (a) map[tutor.id] = a.dozent_bot_id;
      } catch {
        // ignore — best-effort
      }
    }
    this.tutorAttachments = map;
  }

  selectDozent(d: DozentBot) {
    this.selectedDozent = d;
  }

  isTutorLinkedToSelected(tutorId: string): boolean {
    if (!this.selectedDozent) return false;
    return this.tutorAttachments[tutorId] === this.selectedDozent.id;
  }

  isTutorLinkedToOther(tutorId: string): boolean {
    if (!this.selectedDozent) return false;
    const linked = this.tutorAttachments[tutorId];
    return !!linked && linked !== this.selectedDozent.id;
  }

  toggleTutorLink(tutor: Tutor) {
    if (!this.selectedDozent) return;
    if (this.isTutorLinkedToOther(tutor.id)) {
      // Disabled — we don't silently steal links between dozents.
      return;
    }

    if (this.isTutorLinkedToSelected(tutor.id)) {
      this.tutorService.detachDozent(tutor.id).subscribe({
        next: () => {
          delete this.tutorAttachments[tutor.id];
          this.toastr.success(
            this.translate.instant('tutor.dozentLink.detached'),
            tutor.name
          );
        },
      });
    } else {
      this.tutorService.attachDozent(tutor.id, this.selectedDozent.id).subscribe({
        next: () => {
          this.tutorAttachments[tutor.id] = this.selectedDozent!.id;
          this.toastr.success(
            this.translate.instant('tutor.dozentLink.attached'),
            tutor.name
          );
        },
      });
    }
  }
}
